import axios from "axios";
import { useState } from "react";
import { LogOut, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Sticky orange banner shown at the top of every page while the current
 * session is an owner/trainer impersonating a client (read from
 * user.impersonated_by). Clicking the button restores the original session.
 */
export default function ImpersonationBanner() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);

  if (!user || !user.impersonated_by) return null;

  const handleExit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/auth/end-impersonation`, {}, { withCredentials: true });
      toast.success("Retour à votre compte effectué.");
      if (refresh) await refresh();
      // Hard reload to /dashboard so the role-based routing kicks back in
      const role = user.impersonator_role;
      window.location.href = role === "owner" ? "/dashboard/owner" : "/dashboard/trainer";
    } catch {
      toast.error("Impossible de revenir à votre compte. Reconnectez-vous.");
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="impersonation-banner"
      className="fixed top-0 inset-x-0 z-[60] bg-[#E05A3D] text-white shadow-lg border-b-2 border-[#C84B31]"
      style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-5 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <ShieldAlert size={18} className="shrink-0" />
          <p className="text-xs sm:text-sm font-medium truncate">
            Session d&apos;assistance — connecté en tant que{" "}
            <span className="font-bold">{user.name}</span>{" "}
            <span className="opacity-80 hidden sm:inline">({user.email})</span>
          </p>
        </div>
        <button
          type="button"
          onClick={handleExit}
          disabled={busy}
          data-testid="exit-impersonation-btn"
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-[#C84B31] hover:bg-white/90 text-xs sm:text-sm font-bold transition disabled:opacity-60"
        >
          <LogOut size={14} />
          <span className="hidden xs:inline">Revenir à mon compte</span>
          <span className="xs:hidden">Retour</span>
        </button>
      </div>
    </div>
  );
}
